import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Link,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export interface TokenInstructionsLink {
  label: string;
  href: string;
}

export interface TokenInstructionsProps {
  /** Шаги получения токена. Каждый элемент — отдельный пункт нумерованного списка. */
  steps: React.ReactNode[];
  /** Необязательная ссылка(и) на страницу выпуска токена. */
  links?: TokenInstructionsLink[];
  /** Заголовок блока. */
  title?: string;
}

/**
 * Раскрывающийся блок с пошаговой инструкцией по получению токена доступа.
 * Используется в модалках авторизации (Jira, Confluence, Git).
 */
const TokenInstructions: React.FC<TokenInstructionsProps> = ({
  steps,
  links,
  title = 'Как получить токен?',
}) => {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="token-instructions-content"
        id="token-instructions-header"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <HelpOutlineIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          component="ol"
          sx={{
            m: 0,
            pl: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
          }}
        >
          {steps.map((step, index) => (
            <Typography key={index} component="li" variant="body2" color="text.secondary">
              {step}
            </Typography>
          ))}
        </Box>

        {links && links.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            {links.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                {link.label}
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
            ))}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default TokenInstructions;
